import {
  canCreateProjectsWithoutScope,
  canUserExport,
  canUserManageFunctions,
  canUserManageLibrary,
  canUserManageProjectResponsible,
  canManageViews,
  canUserManageScopes,
  canUserManageTemplateOwner,
  canUserManageUsers,
  canViewAnalytics,
  canViewDashboards,
  checkAddScope,
  checkUpdateScope,
  canUserCreateComment,
  canUserDeleteComment,
  canUserUpdateComment,
  canManageInAppContent,
  isGlobalContentAdmin,
  canManageAnyContent,
  canRankFeatures,
  canManageSkills,
  canManageAiModels,
  Comment,
} from '@beeboard/common';
import { defineStore } from 'pinia';
import { useProfileStore } from './profileStore';
import { useProjectStore } from './projectStore';
import { useUserStore } from './userStore';

export const useUserRulesStore = defineStore('userRulesStore', {
  getters: {
    user() {
      return useProfileStore().profile;
    },
  },

  actions: {
    canUserManageUsers() {
      return canUserManageUsers(this.user.roles);
    },

    canUserManageFunctions() {
      return canUserManageFunctions(this.user.roles);
    },

    canUserManageScopes() {
      return canUserManageScopes(this.user.roles);
    },

    canUserManageLibrary() {
      return canUserManageLibrary(this.user.roles);
    },

    canUserManageViews() {
      return canManageViews(this.user.roles);
    },

    canUserExport() {
      return canUserExport(this.user.roles);
    },

    validateCreateScopeRequest(scopeName: string) {
      const allScopes = useUserStore().scopes;

      return checkAddScope(scopeName, allScopes);
    },

    validateUpdateScopeRequest(scopeName: string, scopeId: string) {
      const allScopes = useUserStore().scopes;

      return checkUpdateScope(scopeName, scopeId, allScopes);
    },

    canUserManageProjectResponsible() {
      return canUserManageProjectResponsible(this.user.roles);
    },

    canUserManageTemplateOwner() {
      return canUserManageTemplateOwner(this.user.roles);
    },

    canCreateProjectsWithoutScope() {
      return canCreateProjectsWithoutScope(this.user.roles);
    },

    canViewDashboards() {
      return canViewDashboards(this.user.roles);
    },

    canViewAnalytics() {
      return canViewAnalytics(this.user.roles);
    },

    canUserCreateComment(projectId: string) {
      const projectStore = useProjectStore();
      const members = projectStore.projectMembers(projectId);

      return canUserCreateComment(this.user.id, members);
    },

    canUserEditComment(comment: Comment, projectId: string) {
      const projectStore = useProjectStore();
      const members = projectStore.projectMembers(projectId);
      if (!comment.user) {
        return false; // If comment.user is not defined, user cannot edit
      }

      return canUserUpdateComment(this.user.id, comment, members);
    },

    canUserDeleteComment(comment: Comment, projectId: string) {
      const projectStore = useProjectStore();
      const members = projectStore.projectMembers(projectId);

      if (!comment.user) {
        return false; // If comment.user is not defined, user cannot edit
      }

      return canUserDeleteComment(this.user.id, comment, members);
    },

    canManageInAppContent() {
      return canManageInAppContent(this.user.roles);
    },

    isGlobalContentAdmin() {
      return isGlobalContentAdmin(this.user.roles);
    },

    canManageAnyContent() {
      return canManageAnyContent(this.user.roles);
    },

    canRankFeatures() {
      return canRankFeatures(this.user.roles);
    },

    canManageSkills() {
      return canManageSkills(this.user.roles);
    },

    canManageAiModels() {
      return canManageAiModels(this.user.roles);
    },
  },
});
